/**
 * FIX-CCS-PHOTO-ORIGINAL (CCS Offline Part A, unit A1) — original-quality photo upload.
 *
 * Rulings in force: R8 "No compression. Original bytes, EXIF kept." The camera file is
 * uploaded byte for byte: no canvas redraw, no resize, no quality preset, no AVIF/WebP/JPEG
 * chain, no EXIF stripping. The only two things the phone still does to a photo are:
 *   1. one small JPEG preview (~320 px longest side) for grids and lists — sent alongside
 *      the original, never instead of it;
 *   2. the HEIC exception (spec §4.1.3): a HEIC/HEIF capture is converted ONCE to a JPEG at
 *      full resolution and quality 0.95, and the HEIC's Exif block is copied into the new
 *      JPEG so the capture time and camera GPS survive. The row records
 *      upload_transform = 'heic_to_jpeg'; every other upload records 'none'.
 * A file over the bucket limit is refused with a translated in-app message, never shrunk
 * to fit (spec §4.1.6). The server re-detects the real type from the first bytes and
 * ignores anything the phone claims.
 *
 * This module has no app imports so its pure functions can be unit-tested under Node/Deno.
 */

export type DetectedImageType = 'jpeg' | 'png' | 'webp' | 'heic' | 'unknown';
export type UploadTransform = 'none' | 'heic_to_jpeg';
export type PrepareRefusal = 'too_large' | 'unsupported_type' | 'heic_decode_failed';

/** Mirrors storage.buckets.file_size_limit for the `photos` bucket (A1 migration: 20 MB). */
export const MAX_PHOTO_BYTES = 20 * 1024 * 1024;
export const THUMBNAIL_LONGEST_SIDE_PX = 320;
export const THUMBNAIL_JPEG_QUALITY = 0.8;
export const HEIC_JPEG_QUALITY = 0.95;
/** An APP1 segment length is 16-bit: 0xFFFF − 2 (length bytes) − 6 ("Exif\0\0"). */
const MAX_APP1_TIFF_BYTES = 0xffff - 2 - 6;

export interface PreparedUpload {
  /** The bytes to upload as the evidence photo (the untouched file, or the converted JPEG for HEIC). */
  original: Blob;
  detected: 'jpeg' | 'png' | 'webp';
  transform: UploadTransform;
  /** ~320 px JPEG preview, or null when the phone could not decode the image. */
  thumbnail: Blob | null;
  /** HEIC only: whether the Exif block was carried into the converted JPEG. */
  exifCopied: boolean | null;
}

export type PrepareResult = PreparedUpload | { refused: PrepareRefusal };

export const extensionFor: Record<'jpeg' | 'png' | 'webp', string> = { jpeg: 'jpg', png: 'png', webp: 'webp' };
export const mimeFor: Record<'jpeg' | 'png' | 'webp', string> = { jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' };

const HEIF_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs', 'mif1', 'msf1']);

function ascii(bytes: Uint8Array, off: number, len: number): string {
  let s = '';
  for (let i = off; i < off + len && i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return s;
}

/** Real image type from the first bytes. The phone's claimed MIME type is never consulted. */
export function detectImageType(bytes: Uint8Array): DetectedImageType {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
  if (bytes.length >= 8 && bytes[0] === 0x89 && ascii(bytes, 1, 3) === 'PNG' && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a) return 'png';
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') return 'webp';
  if (bytes.length >= 12 && ascii(bytes, 4, 4) === 'ftyp') {
    const boxLen = ((bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3]) >>> 0;
    const major = ascii(bytes, 8, 4);
    if (HEIF_BRANDS.has(major)) return 'heic';
    // compatible brands follow the minor version
    for (let p = 16; p + 4 <= Math.min(bytes.length, boxLen); p += 4) {
      if (HEIF_BRANDS.has(ascii(bytes, p, 4))) return 'heic';
    }
  }
  return 'unknown';
}

// ---------- HEIC / ISO-BMFF: ftyp -> meta -> iinf/iloc -> Exif item ----------
interface Box { size: number; type: string; hdr: number; off: number }

function readBox(dv: DataView, bytes: Uint8Array, off: number): Box | null {
  if (off + 8 > dv.byteLength) return null;
  let size = dv.getUint32(off);
  const type = ascii(bytes, off + 4, 4);
  let hdr = 8;
  if (size === 1) {
    if (off + 16 > dv.byteLength) return null;
    size = dv.getUint32(off + 8) * 4294967296 + dv.getUint32(off + 12);
    hdr = 16;
  } else if (size === 0) size = dv.byteLength - off;
  return { size, type, hdr, off };
}

/**
 * The TIFF block of a HEIC's Exif item (bytes from the TIFF header "MM"/"II" to the end of the
 * item), or null when the file carries none or uses a construction method this walk does not
 * handle. Ported from the OFFLINE-P2 PHONE_TEST.html walker (proved on the pilot fixture).
 */
export function extractHeifExifTiff(bytes: Uint8Array): Uint8Array | null {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let off = 0;
  let meta: Box | null = null;
  while (off < dv.byteLength) {
    const b = readBox(dv, bytes, off);
    if (!b || b.size < 8) break;
    if (b.type === 'meta') { meta = b; break; }
    off += b.size;
  }
  if (!meta) return null;
  const mEnd = Math.min(meta.off + meta.size, dv.byteLength);
  let q = meta.off + meta.hdr + 4; // meta is a FullBox: version + flags
  let exifItemId: number | null = null;
  let iloc: Box | null = null;
  while (q + 8 <= mEnd) {
    const c = readBox(dv, bytes, q);
    if (!c || c.size < 8) break;
    if (c.type === 'iinf') {
      const v = dv.getUint8(c.off + c.hdr);
      let r = c.off + c.hdr + 4;
      let cnt: number;
      if (v === 0) { cnt = dv.getUint16(r); r += 2; } else { cnt = dv.getUint32(r); r += 4; }
      for (let i = 0; i < cnt && r + 8 <= mEnd; i++) {
        const e = readBox(dv, bytes, r);
        if (!e) break;
        if (e.type === 'infe') {
          const ev = dv.getUint8(e.off + e.hdr);
          let s = e.off + e.hdr + 4;
          if (ev >= 2) {
            let id: number;
            if (ev === 2) { id = dv.getUint16(s); s += 2; } else { id = dv.getUint32(s); s += 4; }
            s += 2; // item_protection_index
            if (ascii(bytes, s, 4) === 'Exif' && exifItemId === null) exifItemId = id;
          }
        }
        r += e.size;
      }
    }
    if (c.type === 'iloc') iloc = c;
    q += c.size;
  }
  if (exifItemId === null || !iloc) return null;
  const ver = dv.getUint8(iloc.off + iloc.hdr);
  let p = iloc.off + iloc.hdr + 4;
  const b1 = dv.getUint8(p), b2 = dv.getUint8(p + 1);
  p += 2;
  const offSize = b1 >> 4, lenSize = b1 & 15, baseSize = b2 >> 4, idxSize = (ver === 1 || ver === 2) ? (b2 & 15) : 0;
  let itemCount: number;
  if (ver < 2) { itemCount = dv.getUint16(p); p += 2; } else { itemCount = dv.getUint32(p); p += 4; }
  const rd = (n: number, at: number): number => {
    if (n === 4) return dv.getUint32(at);
    if (n === 8) return dv.getUint32(at) * 4294967296 + dv.getUint32(at + 4);
    return 0;
  };
  let found: { offset: number; length: number; cm: number } | null = null;
  for (let k = 0; k < itemCount && p + 4 <= mEnd; k++) {
    let itemId: number;
    if (ver < 2) { itemId = dv.getUint16(p); p += 2; } else { itemId = dv.getUint32(p); p += 4; }
    let cm = 0;
    if (ver === 1 || ver === 2) { cm = dv.getUint16(p) & 15; p += 2; }
    p += 2; // data_reference_index
    const base = rd(baseSize, p); p += baseSize;
    const extCount = dv.getUint16(p); p += 2;
    for (let x = 0; x < extCount; x++) {
      if (idxSize) p += idxSize;
      const eo = rd(offSize, p); p += offSize;
      const el = rd(lenSize, p); p += lenSize;
      if (itemId === exifItemId && x === 0) found = { offset: base + eo, length: el, cm };
    }
  }
  if (!found || found.cm !== 0) return null; // construction_method 1 (idat) not handled
  if (found.offset + 4 > dv.byteLength || found.length < 4) return null;
  const tiffHdrOff = dv.getUint32(found.offset); // exif_tiff_header_offset
  let t = found.offset + 4 + tiffHdrOff;
  if (ascii(bytes, found.offset + 4, 4) === 'Exif') t = found.offset + 4 + 6; // "Exif\0\0" prefix
  const end = Math.min(found.offset + found.length, dv.byteLength);
  if (t + 8 > end) return null;
  const bo = ascii(bytes, t, 2);
  if (bo !== 'MM' && bo !== 'II') return null;
  return bytes.slice(t, end);
}

/** True when the JPEG already carries an APP1 Exif segment (used by tests and the HEIC path). */
export function jpegHasExif(bytes: Uint8Array): boolean {
  if (detectImageType(bytes) !== 'jpeg') return false;
  let p = 2;
  while (p + 4 <= bytes.length && bytes[p] === 0xff) {
    const marker = bytes[p + 1];
    if (marker === 0xda || marker === 0xd9) break; // SOS / EOI
    const len = (bytes[p + 2] << 8) | bytes[p + 3];
    if (marker === 0xe1 && ascii(bytes, p + 4, 4) === 'Exif') return true;
    p += 2 + len;
  }
  return false;
}

/**
 * A copy of `jpeg` with an APP1 Exif segment (payload "Exif\0\0" + tiff) inserted right after
 * SOI, or null when the TIFF block does not fit one APP1 segment.
 */
export function insertExifIntoJpeg(jpeg: Uint8Array, tiff: Uint8Array): Uint8Array | null {
  if (detectImageType(jpeg) !== 'jpeg') return null;
  if (tiff.length > MAX_APP1_TIFF_BYTES) return null;
  const len = 2 + 6 + tiff.length;
  const out = new Uint8Array(jpeg.length + 4 + 6 + tiff.length);
  out.set(jpeg.subarray(0, 2), 0);
  out[2] = 0xff; out[3] = 0xe1; out[4] = (len >> 8) & 0xff; out[5] = len & 0xff;
  out.set([0x45, 0x78, 0x69, 0x66, 0x00, 0x00], 6); // "Exif\0\0"
  out.set(tiff, 12);
  out.set(jpeg.subarray(2), 12 + tiff.length);
  return out;
}

async function headBytes(blob: Blob, n: number): Promise<Uint8Array> {
  return new Uint8Array(await blob.slice(0, n).arrayBuffer());
}

/**
 * Size guard + real-type check, run the moment a file is picked. Reads only the first 64 bytes.
 * Refuses: over the bucket limit (never shrunk to fit), or bytes that are not JPEG/PNG/WebP/HEIC.
 */
export type CheckResult = { ok: true; type: Exclude<DetectedImageType, 'unknown'> } | { ok: false; reason: PrepareRefusal };

export async function checkOriginal(file: Blob): Promise<CheckResult> {
  if (file.size > MAX_PHOTO_BYTES) return { ok: false, reason: 'too_large' };
  const type = detectImageType(await headBytes(file, 64));
  if (type === 'unknown') return { ok: false, reason: 'unsupported_type' };
  return { ok: true, type };
}

interface Decoded { width: number; height: number; draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void; close: () => void }

/** Decode with createImageBitmap, falling back to an <img> element. `orientation` = 'from-image' applies Exif rotation; 'none' keeps stored pixels. */
async function decode(blob: Blob, orientation: 'from-image' | 'none'): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(blob, { imageOrientation: orientation });
      return { width: bmp.width, height: bmp.height, draw: (ctx, w, h) => ctx.drawImage(bmp, 0, 0, w, h), close: () => bmp.close() };
    } catch {
      // fall through to <img>
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('image decode failed'));
      el.src = url;
    });
    return { width: img.naturalWidth, height: img.naturalHeight, draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h), close: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))), type, quality);
  });
}

/** One ~320 px JPEG preview for grids/lists. Null when the phone cannot decode the image. */
export async function makeThumbnail(blob: Blob): Promise<Blob | null> {
  let decoded: Decoded | null = null;
  try {
    decoded = await decode(blob, 'from-image');
    const scale = Math.min(1, THUMBNAIL_LONGEST_SIDE_PX / Math.max(decoded.width, decoded.height));
    const w = Math.max(1, Math.round(decoded.width * scale));
    const h = Math.max(1, Math.round(decoded.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    decoded.draw(ctx, w, h);
    return await canvasToBlob(canvas, 'image/jpeg', THUMBNAIL_JPEG_QUALITY);
  } catch {
    return null;
  } finally {
    decoded?.close();
  }
}

/**
 * HEIC exception (spec §4.1.3): decode once, re-encode as JPEG at full resolution and quality
 * 0.95 with the stored pixel orientation kept, then copy the HEIC's Exif block into the JPEG.
 * Returns null when this phone cannot decode HEIC (only iOS Safari can today).
 */
export async function convertHeicToJpeg(file: Blob): Promise<{ blob: Blob; exifCopied: boolean } | null> {
  let decoded: Decoded | null = null;
  try {
    decoded = await decode(file, 'none');
    const canvas = document.createElement('canvas');
    canvas.width = decoded.width; canvas.height = decoded.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    decoded.draw(ctx, decoded.width, decoded.height);
    const jpeg = new Uint8Array(await (await canvasToBlob(canvas, 'image/jpeg', HEIC_JPEG_QUALITY)).arrayBuffer());
    const tiff = extractHeifExifTiff(new Uint8Array(await file.arrayBuffer()));
    const withExif = tiff ? insertExifIntoJpeg(jpeg, tiff) : null;
    return { blob: new Blob([withExif ?? jpeg], { type: 'image/jpeg' }), exifCopied: withExif !== null };
  } catch {
    return null;
  } finally {
    decoded?.close();
  }
}

/**
 * Everything the phone does to a photo before upload. The returned `original` is the exact
 * camera bytes for JPEG/PNG/WebP; only HEIC is transformed (and labelled as such).
 */
export async function prepareOriginalUpload(file: Blob): Promise<PrepareResult> {
  const check = await checkOriginal(file);
  if (check.ok === false) return { refused: check.reason };
  if (check.type === 'heic') {
    const converted = await convertHeicToJpeg(file);
    if (!converted) return { refused: 'heic_decode_failed' };
    if (converted.blob.size > MAX_PHOTO_BYTES) return { refused: 'too_large' };
    return { original: converted.blob, detected: 'jpeg', transform: 'heic_to_jpeg', thumbnail: await makeThumbnail(converted.blob), exifCopied: converted.exifCopied };
  }
  return { original: file, detected: check.type, transform: 'none', thumbnail: await makeThumbnail(file), exifCopied: null };
}

/** MB with one decimal for the size-guard message. */
export function toMb(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1);
}
